/* eslint-disable class-methods-use-this */

import type { AspDomainVariants } from '../apis/client-side/preset/type';
import {
  extractHostname,
  resolveSiteFromHostname,
  resolveSiteInfoFromHostname,
  siteInfo,
} from '../utils/site/hostname';
import { ASPConfigManager } from './aspConfigManager';
import {
  developmentBktv,
  developmentFanza,
  mDevelopmentBktv,
  mDevelopmentFanza,
  mProductionDevWwwPandatv,
  productionDevWwwPandatv,
} from './DevelopmentConfigMappings';
import {
  localFanza,
  localPandatv,
  mLocalFanza,
  mLocalPandatv,
  mProductionLocalFanza,
  mProductionLocalPandatv,
  productionLocalFanza,
  productionLocalPandatv,
  productionLocalSales,
} from './LocalConfigMappings';
import {
  mProductionFanza,
  mProductionPandatv,
  productionFanza,
  productionPandatv,
  productionSales,
} from './ProductionConfigMappings';
import type { CoreConfig, Environment, SettingConfigSiteInfo } from './type';

/** API_URL의 스킴은 페이지 스킴과 일치해야 쿠키/인증이 정상 동작합니다. SSR/빌드에선 https 폴백. */
const resolveCurrentScheme = (): 'http' | 'https' => {
  if (typeof window !== 'undefined') {
    return window.location.protocol === 'http:' ? 'http' : 'https';
  }
  return 'https';
};

class SettingConfigImpl implements CoreConfig {
  environment!: Environment;
  private configMappings!: { [key in Environment]: any };
  private aspConfigManager?: ASPConfigManager | null = null;

  constructor() {
    this.initCoreConfig();
  }

  private initCoreConfig() {
    this.environment = (process.env.ENVIRONMENT_STATUS as Environment) || 'local';
    // TODO(sales): add sales environment mappings for pc/mobile.
    this.configMappings = {
      // local
      local: localPandatv,
      'm-local': mLocalPandatv,
      'production-local-pandatv': productionLocalPandatv,
      'm-production-local-pandatv': mProductionLocalPandatv,
      'production-local-fanza': productionLocalFanza,
      'm-production-local-fanza': mProductionLocalFanza,
      'local-fanza': localFanza,
      'm-local-fanza': mLocalFanza,
      'production-local-sales': productionLocalSales,
      // dev
      'development-bktv': developmentBktv,
      'm-development-bktv': mDevelopmentBktv,
      'production-dev-www': productionDevWwwPandatv,
      'm-production-dev-www': mProductionDevWwwPandatv,
      'development-fanza': developmentFanza,
      'm-development-fanza': mDevelopmentFanza,

      // prod
      'production-pandatv': productionPandatv,
      'm-production-pandatv': mProductionPandatv,
      'production-fanza': productionFanza,
      'm-production-fanza': mProductionFanza,
      'production-sales': productionSales,
    };

    this.aspConfigManager = new ASPConfigManager(this);
  }

  updateApiUrl(url: AspDomainVariants): void {
    this.configMappings[this.environment].API_URL = extractHostname(url);
  }

  get iframeASPConfig() {
    return this.aspConfigManager?.iframeASPConfig;
  }

  get newASPPreset() {
    return this.aspConfigManager?.newASPPreset;
  }

  get aspUtils() {
    return this.aspConfigManager?.aspUtils;
  }
  async aspInfoSettled() {
    return this.aspConfigManager?.aspInfoSettled();
  }

  /** @description ASP 관련 설정을 셋업 * @returns ASPConfig | undefined */
  private getConfig(key: keyof SettingConfigSiteInfo) {
    return this.configMappings[this.environment][key];
  }

  get ENVIRONMENT() {
    return this.environment;
  }

  get BASE_URL() {
    return this.getConfig('BASE_URL');
  }

  get API_URL(): string {
    return `${resolveCurrentScheme()}://${this.getConfig('API_URL')}`;
  }

  get CACHE_API_URL(): string {
    const host = this.getConfig('CACHE_API_URL') ?? this.getConfig('API_URL');
    return `${resolveCurrentScheme()}://${host}`;
  }

  get PC_URL() {
    return this.getConfig('PC_URL');
  }

  get EVENT_URL() {
    return this.getConfig('EVENT_URL') as string;
  }

  get S3_BUCKET_URL() {
    return this.getConfig('S3_BUCKET_URL');
  }

  get PANDA_CHAT_SOCKET() {
    return this.getConfig('PANDA_CHAT_SOCKET') as string;
  }

  get SENTRY_DSN() {
    if (this.environment === 'production-pandatv')
      return 'https://06e4ebbfe92b7a0fcefd3b915069be45@o4506109302538240.ingest.us.sentry.io/4507326943068160';
    if (this.environment === 'm-production-pandatv') {
      return 'https://42ef9f4a746cec992f8b838276b28a36@o4506109302538240.ingest.us.sentry.io/4507332541546496';
    }
    return '';
  }

  get CURRENT_SITE() {
    if (typeof window === 'undefined') return 'pandalive';
    return resolveSiteFromHostname(window.location.hostname);
  }

  get CURRENT_SITE_INFO() {
    if (typeof window === 'undefined') return siteInfo.pandalive;
    return resolveSiteInfoFromHostname(window.location.hostname);
  }

  get RECAPTCHA_SITE_KEY() {
    // if (!this.isProd) return '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Google test key
    return '6Lcdq9cUAAAAAO01X-u4GDXwYAY8-Sa-Q4ksE_xE';
  }

  get isProd() {
    return (
      this.environment === 'production-pandatv' ||
      this.environment === 'm-production-pandatv' ||
      this.environment === 'production-fanza' ||
      this.environment === 'm-production-fanza'
    );
  }

  get SHORT_SHA() {
    return process.env.SHORT_SHA || process.env.NEXT_PUBLIC_SHORT_SHA || '0';
  }

  get isMoWeb() {
    return this.environment.startsWith('m-');
  }

  get isFanza() {
    return this.environment.includes('fanza');
  }

  get isSales() {
    return this.environment.includes('sales');
  }

  get LOCALE() {
    if (this.isFanza) return 'jp';
    return 'kr';
  }

  get LANG(): 'ja' | 'en' | 'ko' {
    if (this.isFanza) return 'ja';
    if (this.isSales) return 'en';
    return 'ko';
  }
}

const SettingConfig = new SettingConfigImpl();
export default SettingConfig;
